import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Upload, Trash2, Sparkles, Star, Award, Pencil, Plus, X, Check, Lightbulb, Search, ChevronDown } from "lucide-react";
import GenreArtControls from "../components/GenreArtControls";
import ScreenOrderManager from "../components/ScreenOrderManager";
import AdminNotifications, { visitLine } from "../components/AdminNotifications";
import AdminFeedback from "../components/AdminFeedback";
import AdminAtRisk from "../components/AdminAtRisk";
import AdminMailers, { NOT_SENT } from "../components/AdminMailers";
import AdminStats, { UserPlays } from "../components/AdminStats";
import { LightsEditor, SchemeDot, normScheme, Backlight, songLights, songMoodName, schemeColor } from "../components/MiniPlayer";

const EMPTY = {
  id: null,
  title: "", artist: "", album: "", description: "",
  lyrics: "", captions: "", textLoaded: true,   // lyrics and captions are kept apart from the song; textLoaded = they are in the boxes (or the song is new)
  cover_url: "", video_url: "", preview_url: "", audio_url: "",
  genres: [], moods: [], primary_mood: "", tags: "", language: "", country: "",
  featured: false, premium: false, explicit: false, dan_pick: false, announce: false,
};

// LYRICS-FROM-CAPTIONS:START
// The words of a captions file (.srt or .vtt text) as lyrics: no numbers or times, the short caption pieces joined into
// sentences, and a blank line where the singing pauses for 3 seconds or more (a new verse).
const PAUSE_BREAK = 3;   // seconds of quiet that start a new verse
const LINE_CAP = 140;    // a line is cut here even when no sentence ended
function lyricsFromCaptions(text) {
  const clock = /(?:(\d{1,3}):)?(\d{1,2}):(\d{2})[.,](\d{1,3})/;
  const secs = (m) => Number(m[1] || 0) * 3600 + Number(m[2]) * 60 + Number(m[3]) + Number("0." + m[4]);
  const blocks = String(text || "").replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n").split(/\n\s*\n/);
  const cues = [];
  for (const block of blocks) {
    const lines = block.split("\n");
    const at = lines.findIndex((l) => l.includes("-->"));
    if (at < 0) continue;
    const [a, b] = lines[at].split("-->");
    const st = clock.exec(a), en = clock.exec(b);
    if (!st || !en) continue;
    const words = lines.slice(at + 1).join(" ").replace(/<[^>]*>/g, "").replace(/\{[^}]*\}/g, "").replace(/\s+/g, " ").trim();
    if (words) cues.push({ s: secs(st), e: secs(en), t: words });
  }
  cues.sort((x, y) => x.s - y.s);
  const out = [];
  let line = "", last = null;
  const flush = () => { if (line) out.push(line); line = ""; };
  for (const c of cues) {
    if (last !== null && c.s - last >= PAUSE_BREAK) {
      flush();
      if (out.length && out[out.length - 1] !== "") out.push("");
    }
    line = line ? `${line} ${c.t}` : c.t;
    if (/[.!?\u2026]["'\u201d\u2019)]*$/.test(line) || line.length >= LINE_CAP) flush();
    last = c.e;
  }
  flush();
  return out.join("\n").trim();
}
// LYRICS-FROM-CAPTIONS:END

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);
  const [genres, setGenres] = useState([]);
  const [moods, setMoods] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState({});
  const [find, setFind] = useState("");   // what is typed in the catalog's search box
  const formBox = useRef(null);           // the Add new video / Edit video box
  const editing = !!form.id;

  const load = async () => {
    const [s, v, g, m] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/videos?limit=500"),
      api.get("/genres"),
      api.get("/admin/moods"),
    ]);
    setStats(s.data); setVideos(v.data); setGenres(g.data); setMoods(m.data);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);
  const refreshStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});

  // For the New song box: how many members would get the email, and whether the mailing address is saved yet.
  const [mailInfo, setMailInfo] = useState(null);
  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    const get = () => api.get("/admin/mail/audience").then((r) => setMailInfo(r.data)).catch(() => {});
    get();
    window.addEventListener("dtm-mail-address", get);
    return () => window.removeEventListener("dtm-mail-address", get);
  }, [user]);

  if (!user || user.role !== "admin") return (
    <div className="py-20 text-center">
      <h1 className="font-display text-3xl mb-3">Admin access required</h1>
      <Link to="/login" className="text-cyan-400 hover:underline">Log in as admin</Link>
    </div>
  );

  // Catalog search: every word typed has to match somewhere in the song (title, artist, album, genre,
  // mood, tags, language, country), so "dan rap" finds Dan's rap songs. "premium" and "hero" find
  // those songs too.
  const genreName = Object.fromEntries(genres.map((g) => [g.slug, g.name]));
  const words = find.toLowerCase().split(/\s+/).filter(Boolean);
  const shownVideos = !words.length ? videos : videos.filter((v) => {
    const hay = [
      v.title, v.artist, v.album, v.language, v.country, v.tags,
      ...(v.genres || []).flatMap((s) => [s, genreName[s]]),
      ...(v.moods || []),
      v.premium ? "premium" : "", v.featured ? "featured hero" : "", v.dan_pick ? "pick picks" : "",
    ].filter(Boolean).join(" ").toLowerCase();
    return words.every((w) => hay.includes(w));
  });

  const upload = async (file, kind, key) => {
    setUploading({ ...uploading, [key]: true });
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [key]: data.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${data.url}` : data.url }));
      toast.success(`${kind} uploaded`);
    } catch (e) { toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`); }
    finally { setUploading({ ...uploading, [key]: false }); }
  };

  const toggleGenreInForm = (slug) => {
    setForm((f) => ({
      ...f,
      genres: f.genres.includes(slug) ? f.genres.filter((s) => s !== slug) : [...f.genres, slug],
    }));
  };

  const toggleMoodInForm = (name) => {
    setForm((f) => ({
      ...f,
      moods: f.moods.includes(name) ? f.moods.filter((m) => m !== name) : [...f.moods, name],
      // taking the primary mood off the song means the song has no pick any more (its first mood counts)
      primary_mood: f.primary_mood === name && f.moods.includes(name) ? "" : f.primary_mood,
    }));
  };

  // A mood was renamed in the panel below: if a song is open in the form above
  // with the old spelling ticked, switch it to the new one so saving keeps it.
  const moodRenamed = (oldName, newName) => {
    setForm((f) => (f.moods.includes(oldName)
      ? { ...f, moods: f.moods.map((x) => (x === oldName ? newName : x)).filter((x, i, a) => a.indexOf(x) === i), primary_mood: f.primary_mood === oldName ? newName : f.primary_mood }
      : f));
  };

  const startEdit = (v) => {
    setForm({
      id: v.id,
      title: v.title || "", artist: v.artist || "", album: v.album || "", description: v.description || "",
      lyrics: "", captions: "", textLoaded: false,
      cover_url: v.cover_url || "", video_url: v.video_url || "", preview_url: v.preview_url || "", audio_url: v.audio_url || "",
      genres: v.genres || [],
      moods: v.moods || [],
      primary_mood: v.primary_mood || "",
      tags: (v.tags || []).join(", "),
      language: v.language || "", country: v.country || "",
      featured: !!v.featured, premium: !!v.premium, explicit: !!v.explicit, dan_pick: !!v.dan_pick,
    });
    // The song's lyrics and captions are kept apart from the song, so they are fetched for the two boxes. Until they have
    // arrived the boxes are locked and saving leaves lyrics and captions exactly as they are.
    api.get(`/admin/videos/${v.id}/text`)
      .then((r) => setForm((f) => (f.id === v.id ? { ...f, lyrics: r.data.lyrics || "", captions: r.data.captions || "", textLoaded: true } : f)))
      .catch(() => toast.error("Could not load this song's lyrics and captions. Saving will leave them as they are."));
    showSongBox();
  };

  // The pencil fills the box above, so bring that box into view. If its top is already on the screen the page stays exactly where it is;
  // otherwise the page moves so the top of the box sits just under the menu bar (not all the way to the top of the page).
  const showSongBox = () => {
    const el = formBox.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    if (top >= 72 && top < window.innerHeight * 0.5) return;
    const calm = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    el.scrollIntoView({ behavior: calm ? "auto" : "smooth", block: "start" });
  };

  const cancelEdit = () => setForm(EMPTY);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title, artist: form.artist, album: form.album || null, description: form.description || null,
        cover_url: form.cover_url, video_url: form.video_url, preview_url: form.preview_url || null, audio_url: form.audio_url || null,
        genres: form.genres,
        moods: form.moods,
        primary_mood: form.moods.includes(form.primary_mood) ? form.primary_mood : "",
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        language: form.language || null, country: form.country || null,
        featured: form.featured, premium: form.premium, explicit: form.explicit, dan_pick: form.dan_pick,
      };
      // The captions box is checked first, so a file that cannot be read stops here and the song is not half saved.
      let skipped = 0;
      if (form.textLoaded && form.captions.trim()) {
        const chk = await api.post("/admin/captions/check", { captions: form.captions });
        skipped = (chk.data && chk.data.skipped) || 0;
      }
      let songId = form.id;
      if (editing) {
        await api.patch(`/admin/videos/${form.id}`, payload);
        toast.success("Video updated");
      } else {
        const res = await api.post("/admin/videos", { ...payload, announce: !!form.announce });
        songId = res.data && res.data.id;
        const a = res.data && res.data.announce;     // only there when the New song box was ticked
        if (a && a.queued) toast.success(`Video added. Emailing ${a.queued} members now.`);
        else if (a) toast.error(NOT_SENT[a.reason] || "The song was added, but the email was not sent.");
        else toast.success("Video added");
        if (a) window.dispatchEvent(new Event("dtm-mail-refresh"));
      }
      if (form.textLoaded && songId && (editing || form.lyrics.trim() || form.captions.trim())) {
        try {
          await api.put(`/admin/videos/${songId}/text`, { lyrics: form.lyrics, captions: form.captions });
          if (skipped) toast.message(`${skipped} caption line${skipped === 1 ? "" : "s"} had times that did not make sense and ${skipped === 1 ? "was" : "were"} left out.`);
        } catch (err) {
          // The song itself is saved. Keep the form open on that song so nothing typed is lost and a second press cannot add it twice.
          toast.error("The song was saved, but its lyrics and captions were not: " + (err.response?.data?.detail || "something went wrong"));
          setForm((f) => ({ ...f, id: songId, textLoaded: true }));
          load();
          return;
        }
      }
      setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // The captions box can be filled from an .srt (or .vtt) file chosen on this computer.
  const readCaptionsFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      setForm((f) => ({ ...f, captions: text }));
    } catch (e) { toast.error("Could not read that file."); }
  };

  // Fills the Lyrics box from the Captions box: the words only, no times. It never replaces words that are already there.
  const wordsFromCaptions = () => {
    if (!form.captions.trim()) { toast.error("The Captions box is empty. Add the captions first."); return; }
    if (form.lyrics.trim()) { toast.error("The Lyrics box already has words. Clear it first if you want these instead."); return; }
    const words = lyricsFromCaptions(form.captions);
    if (!words) { toast.error("I could not find any words in the captions."); return; }
    setForm((f) => ({ ...f, lyrics: words }));
    toast.message("Words copied. Read them over, then press Save changes.");
  };

  const del = async (id) => {
    if (!confirm("Delete this video?")) return;
    await api.delete(`/admin/videos/${id}`);
    if (form.id === id) setForm(EMPTY);
    load();
  };

  const togglePremium = async (v) => {
    try {
      await api.patch(`/admin/videos/${v.id}`, {
        title: v.title, artist: v.artist, album: v.album, description: v.description,
        cover_url: v.cover_url, video_url: v.video_url, preview_url: v.preview_url || null, audio_url: v.audio_url,
        genres: v.genres || [], moods: v.moods || [], tags: v.tags || [],
        language: v.language, country: v.country,
        release_date: v.release_date, featured: !!v.featured,
        premium: !v.premium, explicit: !!v.explicit,
        duration_seconds: v.duration_seconds,
      });
      toast.success(v.premium ? "Premium removed" : "Marked as Premium");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // The medal turns "Dan The Man's Pick" on or off for one song. Premium rules stay the same for a pick.
  const setPick = async (v) => {
    try {
      const { data } = await api.post(`/admin/videos/${v.id}/pick`);
      toast.success(data.dan_pick ? `"${v.title}" is now one of Dan The Man's Picks` : `"${v.title}" is no longer one of Dan The Man's Picks`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // The star turns "featured" on or off for one song. Any number of songs can be featured.
  const setFeatured = async (v) => {
    try {
      const { data } = await api.post(`/admin/videos/${v.id}/feature`);
      toast.success(data.featured ? `"${v.title}" is now featured` : `"${v.title}" is no longer featured`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-8">Admin</h1>
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
          {[["Users", stats.users], ["Premium", stats.premium_users], ["Videos", stats.videos], ["Playlists", stats.playlists], ["Favorites", stats.favorites], ["Plays", stats.total_plays]].map(([k, v]) => (
            <div key={k} className="bg-zinc-900/60 border border-white/10 rounded-xl p-4">
              <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500">{k}</div>
              <div className="font-display text-2xl font-bold mt-1">{v?.toLocaleString?.() ?? v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-10">
        <AdminStats />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Wide screen: the Add new video form with the Genres and Moods boxes under it on the left, everything else on the right, so there is no empty gap under the Add video button. Phone: one column, the same order as before. */}
        <div className="max-lg:contents lg:space-y-8 lg:min-w-0">
        <form ref={formBox} onSubmit={submit} className="max-lg:order-1 scroll-mt-24 bg-zinc-900/60 border border-white/10 rounded-2xl p-6 space-y-3" data-testid="admin-video-form">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-2xl font-bold">
              {editing ? "Edit video" : "Add new video"}
            </h2>
            {editing && (
              <button type="button" onClick={cancelEdit} data-testid="admin-cancel-edit"
                className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
                <X className="w-3.5 h-3.5" /> Cancel edit
              </button>
            )}
          </div>
          {[
            ["title", "Title *", true],
            ["artist", "Artist *", true],
            ["album", "Album"],
            ["language", "Language (e.g. English, Japanese)"],
            ["country", "Country/Region"],
            ["tags", "Tags (comma separated)"],
          ].map(([k, ph, req]) => (
            <input key={k} data-testid={`admin-input-${k}`} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50" />
          ))}

          {/* Multi-select genres */}
          <div>
            <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500 mb-2">
              Genres ({form.genres.length} selected)
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1" data-testid="admin-genres-picker">
              {genres.length === 0 && <span className="text-xs text-zinc-500">No genres yet — add one below.</span>}
              {genres.map((g) => {
                const on = form.genres.includes(g.slug);
                return (
                  <button
                    type="button"
                    key={g.slug}
                    data-testid={`admin-genre-pick-${g.slug}`}
                    onClick={() => toggleGenreInForm(g.slug)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-cyan-400/20 border-cyan-400/60 text-cyan-300" : "bg-white/5 border-white/10 text-zinc-400 hover:border-cyan-400/40"}`}
                  >
                    {g.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Multi-select moods */}
          <div>
            <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500 mb-2">
              Moods ({form.moods.length} selected)
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1" data-testid="admin-moods-picker">
              {moods.length === 0 && <span className="text-xs text-zinc-500">No moods yet — add one below.</span>}
              {moods.map((m) => {
                const on = form.moods.includes(m.name);
                return (
                  <button
                    type="button"
                    key={m.id}
                    data-testid={`admin-mood-pick-${m.name}`}
                    onClick={() => toggleMoodInForm(m.name)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-fuchsia-400/20 border-fuchsia-400/60 text-fuchsia-300" : "bg-white/5 border-white/10 text-zinc-400 hover:border-fuchsia-400/40"}`}
                  >
                    {m.name}
                  </button>
                );
              })}
            </div>
            {form.moods.length > 0 && (
              <div className="mt-3" data-testid="admin-primary-mood">
                <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500 mb-2">Primary mood (its lights play for this song)</div>
                <div className="flex flex-wrap gap-1.5">
                  {form.moods.map((name) => {
                    const chosen = form.moods.includes(form.primary_mood) ? form.primary_mood : form.moods[0];
                    const on = name === chosen;
                    const m = moods.find((x) => x.name === name);
                    return (
                      <button
                        type="button" key={name} aria-pressed={on} data-testid={`admin-primary-pick-${name}`}
                        onClick={() => setForm((f) => ({ ...f, primary_mood: name }))}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-cyan-400/15 border-cyan-400/70 text-cyan-200" : "bg-white/5 border-white/10 text-zinc-400 hover:border-cyan-400/40"}`}
                      >
                        <SchemeDot scheme={(m && m.lights) || { style: "off" }} size={10} />
                        {on ? "★ " : ""}{name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-zinc-500 mt-1.5" data-testid="admin-primary-note">
                  {form.moods.includes(form.primary_mood) ? "You picked this one." : "You have not picked one, so the first mood counts."}
                </p>
              </div>
            )}
          </div>

          <textarea data-testid="admin-input-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm h-20 focus:outline-none focus:border-cyan-400/50" />

          <div className="space-y-1" data-testid="admin-lyrics-box">
            <div className="flex items-center justify-between gap-2">
              <label className="block text-xs font-mono-ui uppercase tracking-widest text-zinc-500">Lyrics (optional)</label>
              <button type="button" data-testid="admin-lyrics-from-captions" onClick={wordsFromCaptions} disabled={!form.textLoaded}
                className="shrink-0 px-3 py-1.5 rounded-lg border border-white/15 text-xs hover:bg-white/5 disabled:opacity-60">Copy words from captions</button>
            </div>
            <textarea data-testid="admin-input-lyrics" value={form.lyrics} disabled={!form.textLoaded}
              onChange={(e) => setForm({ ...form, lyrics: e.target.value })}
              placeholder={form.textLoaded ? "The words of the song, one line per line. They show on the song page and give Google the words." : "Loading the lyrics…"}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm h-40 focus:outline-none focus:border-cyan-400/50 disabled:opacity-60" />
            <p className="text-xs text-zinc-500">On an explicit song the lyrics stay hidden until a member passes the 18+ check.</p>
          </div>

          <div className="space-y-1" data-testid="admin-captions-box">
            <div className="flex items-center justify-between gap-2">
              <label className="block text-xs font-mono-ui uppercase tracking-widest text-zinc-500">Captions (optional)</label>
              <label data-testid="admin-captions-file" className="shrink-0 px-3 py-1.5 rounded-lg border border-white/15 text-xs hover:bg-white/5 cursor-pointer flex items-center gap-1">
                <Upload className="w-3 h-3" /> Choose .srt file
                <input type="file" accept=".srt,.vtt,text/plain,text/vtt" className="hidden" disabled={!form.textLoaded}
                  onChange={(e) => { readCaptionsFile(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
            <textarea data-testid="admin-input-captions" value={form.captions} disabled={!form.textLoaded}
              onChange={(e) => setForm({ ...form, captions: e.target.value })}
              placeholder={form.textLoaded ? "Paste the timed lines here (an .srt file from Shotcut), or use Choose .srt file. Songs with captions get a CC button in the player." : "Loading the captions…"}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono-ui h-28 focus:outline-none focus:border-cyan-400/50 disabled:opacity-60" />
            <p className="text-xs text-zinc-500">Only people playing the whole song see captions. A Premium song's preview clip has none.</p>
          </div>

          {[["cover", "cover_url", "image/*"], ["video", "video_url", "video/*"], ["preview", "preview_url", "video/*"], ["audio", "audio_url", "audio/*"]].map(([kind, key, accept]) => (
            <div key={kind} className="flex gap-2">
              <input data-testid={`admin-input-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                placeholder={key === "video_url" ? "Video URL (paste any public https direct-mp4 link, e.g. R2)" : key === "preview_url" ? "Preview link (optional short clip URL, e.g. R2 preview mp4)" : `${kind} URL`}
                className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50" />
              <label data-testid={`admin-upload-${kind}`} className="shrink-0 px-3 py-2 rounded-lg border border-white/15 text-sm hover:bg-white/5 cursor-pointer flex items-center gap-1">
                <Upload className="w-3 h-3" /> {uploading[key] ? "…" : kind}
                <input type="file" accept={accept} className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], kind === "preview" ? "video" : kind, key)} />
              </label>
            </div>
          ))}

          <div className="flex gap-4 text-sm text-zinc-300 pt-2">
            {["featured", "premium", "explicit", "dan_pick"].map((k) => (
              <label key={k} className="flex items-center gap-1.5">
                <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`admin-check-${k}`} /> {k === "dan_pick" ? "Dan The Man's pick" : k}
              </label>
            ))}
          </div>
          {!editing && (
            <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm" data-testid="admin-announce-box">
              <label className="flex items-start gap-2 text-zinc-200">
                <input type="checkbox" className="mt-1" checked={!!form.announce} disabled={!!mailInfo && !mailInfo.address_set}
                  onChange={(e) => setForm({ ...form, announce: e.target.checked })} data-testid="admin-check-announce" />
                <span>
                  <b>New song</b>: email my members about it when I press Add video
                  <span className="block text-xs text-zinc-500 mt-0.5" data-testid="admin-announce-note">
                    {mailInfo && !mailInfo.address_set
                      ? "First save your mailing address under Email to members, further down this page."
                      : form.announce
                        ? (mailInfo ? `Emails ${mailInfo.count} members when you press Add video. It cannot be undone.` : "Emails your members when you press Add video. It cannot be undone.")
                        : "Leave this unticked and no email goes out."}
                  </span>
                </span>
              </label>
            </div>
          )}
          <button data-testid="admin-submit-video" className="w-full py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 mt-2">
            {editing ? "Save changes" : "Add video"}
          </button>
        </form>
        <div className="max-lg:order-4"><GenreManager genres={genres} onChange={load} /></div>
        <div className="max-lg:order-5"><MoodManager moods={moods} onChange={load} onRenamed={moodRenamed} /></div>
        </div>

        <div className="max-lg:contents lg:space-y-8">
          <div className="max-lg:order-2">
            <h2 className="font-display text-2xl font-bold mb-4" data-testid="admin-catalog-heading">
              Catalog ({words.length ? `${shownVideos.length} of ${videos.length}` : videos.length})
            </h2>
            <div className="relative mb-3">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <input type="text" inputMode="search" autoComplete="off" spellCheck={false} value={find} onChange={(e) => setFind(e.target.value)}
                placeholder="Search your songs: title, artist, genre, mood…" aria-label="Search the catalog" data-testid="admin-catalog-search"
                className="w-full h-11 bg-white/5 border border-white/10 rounded-full pl-10 pr-11 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-cyan-400/60" />
              {find && (
                <button type="button" onClick={() => setFind("")} aria-label="Clear the search" data-testid="admin-catalog-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-2">
              {shownVideos.map((v) => (
                <div key={v.id} className={`flex items-center gap-3 p-2 bg-zinc-900/60 border rounded-lg ${v.featured ? "border-cyan-400/50" : "border-white/10"}`} data-testid={`admin-video-${v.id}`}>
                  <img src={v.cover_url} className="w-14 h-14 rounded object-cover" alt="" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                      {v.title}
                      {v.featured && <span className="text-[9px] font-mono-ui uppercase tracking-widest text-cyan-300 border border-cyan-400/40 rounded px-1">Featured</span>}
                      {v.dan_pick && <span className="text-[9px] font-mono-ui uppercase tracking-widest text-fuchsia-300 border border-fuchsia-400/40 rounded px-1">Pick</span>}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{v.artist} • {v.genres?.join(", ") || "no genres"}</div>
                  </div>
                  <button
                    data-testid={`admin-toggle-featured-${v.id}`}
                    onClick={() => setFeatured(v)}
                    title={v.featured ? "Featured: tap to take it off Featured" : "Feature this song (Home banner and Featured Videos row)"}
                    className={`p-2 rounded-full border ${v.featured ? "bg-cyan-400/15 border-cyan-400/60 text-cyan-300" : "border-white/10 text-zinc-500 hover:text-cyan-300 hover:border-cyan-400/40"}`}
                  >
                    <Star className="w-4 h-4" style={{ fill: v.featured ? "currentColor" : "transparent" }} />
                  </button>
                  <button
                    data-testid={`admin-toggle-premium-${v.id}`}
                    onClick={() => togglePremium(v)}
                    title={v.premium ? "Deselect Premium" : "Mark as Premium"}
                    className={`p-2 rounded-full border ${v.premium ? "bg-amber-500/15 border-amber-400/50 text-amber-400" : "border-white/10 text-zinc-500 hover:text-amber-400 hover:border-amber-400/40"}`}
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`admin-toggle-pick-${v.id}`}
                    onClick={() => setPick(v)}
                    title={v.dan_pick ? "Dan The Man's Pick: tap to remove it" : "Make this one of Dan The Man's Picks"}
                    className={`p-2 rounded-full border ${v.dan_pick ? "bg-fuchsia-400/15 border-fuchsia-400/60 text-fuchsia-300" : "border-white/10 text-zinc-500 hover:text-fuchsia-300 hover:border-fuchsia-400/40"}`}
                  >
                    <Award className="w-4 h-4" />
                  </button>
                  <button data-testid={`admin-edit-${v.id}`} onClick={() => startEdit(v)} className="p-2 rounded-full hover:bg-white/10 text-zinc-400 hover:text-white">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`admin-delete-${v.id}`} onClick={() => del(v.id)} className="p-2 rounded-full hover:bg-crimson/10 text-crimson"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {videos.length === 0 && <div className="text-zinc-500 text-sm py-6 text-center">No videos in the catalog yet.</div>}
              {videos.length > 0 && shownVideos.length === 0 && (
                <div className="text-zinc-500 text-sm py-6 text-center" data-testid="admin-catalog-nomatch">No songs match “{find.trim()}”. Try fewer letters.</div>
              )}
            </div>
          </div>

          <div className="max-lg:order-3"><SampleMaker onChange={load} videos={videos} moods={moods} /></div>
          <div className="max-lg:order-6 space-y-8">
          <ScreenOrderManager only="home:featured" title="Featured Videos"
            note="Every featured song, in the order visitors see it. The first one shows first in the big banner at the top of Home, and the banner moves through the rest in this order. Turn a song on or off with its star in the catalog above." />
          <ScreenOrderManager only="home:dan_picks" title="Dan The Man's Picks"
            note="The songs you marked with the medal icon in the catalog above, in the order the Dan The Man's Picks row shows them on Home. A Premium song stays Premium here too." />
          <ScreenOrderManager />
          </div>
        </div>
      </div>

      <div className="mt-12">
        <UserManager onChange={refreshStats} />
      </div>

      <div className="mt-12">
        <AdminAtRisk />
      </div>

      <div className="mt-12">
        <AdminFeedback />
      </div>

      <div className="mt-12">
        <AdminMailers />
      </div>

      <div className="mt-12">
        <AdminNotifications />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users sub-panel: find a person, give or take away complimentary Premium
// ---------------------------------------------------------------------------
const COMP_CHOICES = [
  { kind: "complimentary", label: "Until I remove it", note: "Stays on until you take it off" },
  { kind: "trial", label: "7-day free trial", note: "Switches off by itself after 7 days" },
  { kind: "month", label: "1 month free", note: "Switches off by itself after 30 days" },
];
const USER_LIMIT = 50;
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

function PlanBadge({ u }) {
  const styles = {
    admin: [u.is_owner ? "Owner" : "Admin", "border-cyan-400/40 text-cyan-300"],
    paid: ["Premium, pays through Stripe", "border-amber-400/40 text-amber-300"],
    premium: ["Premium", "border-amber-400/40 text-amber-300"],
    complimentary: ["Complimentary Premium", "border-emerald-400/40 text-emerald-300"],
    trial: [`7-day trial, ends ${fmtDay(u.premium_until)}`, "border-emerald-400/40 text-emerald-300"],
    month: [`Free month, ends ${fmtDay(u.premium_until)}`, "border-emerald-400/40 text-emerald-300"],
    free: ["Free", "border-white/15 text-zinc-400"],
    banned: ["Banned", "border-crimson/50 text-crimson"],
  };
  const [text, cls] = styles[u.plan] || styles.free;
  return <span data-testid={`admin-user-plan-${u.email}`} className={`inline-block self-start sm:self-auto px-2.5 py-1 rounded-full border text-xs font-mono-ui ${cls}`}>{text}</span>;
}

// A small look at how many accounts are being made, so a wave of fake sign-ups stands out.
// The owner can clear out accounts that never confirmed their email (only exists while email confirmation is on).
function SignupWatch({ version, onPurged }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/admin/signup-stats").then((r) => { if (alive) setSt(r.data); }).catch(() => { if (alive) setSt(null); });
    return () => { alive = false; };
  }, [version]);
  if (!st) return null;
  const purge = async () => {
    if (!confirm(`Delete ${st.unconfirmed_old} accounts that never confirmed their email and are older than ${st.days} days? This cannot be undone. Admins and anyone with Premium are never touched.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/unverified?days=${st.days}`);
      toast.success(data.remaining ? `Deleted ${data.deleted}. ${data.remaining} more are left, press the button again.` : `Deleted ${data.deleted} unconfirmed accounts`);
      onPurged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete these accounts"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="signup-watch" className="mb-4 rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
      <div data-testid="signup-watch-counts" className="text-zinc-300">
        New accounts: <b>{st.joined_24h}</b> in the last 24 hours, <b>{st.joined_7d}</b> in the last 7 days.
        {st.unconfirmed_total > 0 && <> <b data-testid="signup-watch-unconfirmed">{st.unconfirmed_total}</b> have not confirmed their email.</>}
      </div>
      {!st.verification_on && st.joined_24h >= 20 && (
        <p data-testid="signup-watch-hint" className="text-xs text-amber-300 mt-1">That is a lot of new accounts in one day. If they look fake, tell Claude to turn on email confirmation and the human check.</p>
      )}
      {st.can_purge && st.unconfirmed_old > 0 && (
        <button type="button" disabled={busy} onClick={purge} data-testid="purge-unconfirmed"
          className="mt-2 w-full sm:w-auto px-3 py-2 rounded-lg border border-crimson/50 text-crimson hover:bg-crimson/10 text-sm disabled:opacity-50">
          Delete {st.unconfirmed_old} unconfirmed accounts older than {st.days} days
        </button>
      )}
    </div>
  );
}

function UserManager({ onChange }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openId, setOpenId] = useState(null);   // the person whose three choices are showing
  const [playsId, setPlaysId] = useState(null);   // the person whose list of played songs is showing
  const [busyId, setBusyId] = useState(null);
  const [statsKey, setStatsKey] = useState(0);     // bumped whenever the sign-up numbers may have changed
  const [reloadKey, setReloadKey] = useState(0);   // bumped to fetch the list again

  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      api.get(`/admin/users?limit=${USER_LIMIT}&q=${encodeURIComponent(q.trim())}`)
        .then((r) => { if (alive) { setUsers(r.data); setLoaded(true); } })
        .catch(() => { if (alive) { setUsers([]); setLoaded(true); } });
    }, q ? 250 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [q, reloadKey]);

  const who = (u) => u.name || u.email;
  const swap = (fresh) => setUsers((list) => list.map((x) => (x.id === fresh.id ? fresh : x)));

  const give = async (u, kind) => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/comp`, { kind });
      swap(data); setOpenId(null);
      toast.success(kind === "complimentary" ? `${who(u)} now has complimentary Premium` : `${who(u)} has Premium until ${fmtDay(data.premium_until)}`);
      if (onChange) onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change Premium"); }
    finally { setBusyId(null); }
  };

  const giveWeek = async (u) => {
    if (!confirm(`Give ${who(u)} a free week of Premium, and email them to say so? It adds a week each time you press it.`)) return;
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/free-week`);
      swap(data); setOpenId(null);
      if (data.free_week_email_sent) toast.success(`${who(u)} has Premium until ${fmtDay(data.premium_until)}, and the email was sent`);
      else toast.warning(`${who(u)} has Premium until ${fmtDay(data.premium_until)}, but the email could not be sent. Let them know yourself.`);
      if (onChange) onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not give the free week"); }
    finally { setBusyId(null); }
  };

  const setRole = async (u, role) => {
    const ask = role === "admin"
      ? `Make ${who(u)} an admin? Admins can add and delete songs, manage users and give Premium.${u.plan === "paid" ? " They are paying through Stripe, and admins get Premium free, so ask them to cancel their subscription." : ""} You can remove admin access here again at any time.`
      : `Remove admin access from ${who(u)}? They become a regular account right away and lose this Admin page.`;
    if (!confirm(ask)) return;
    setBusyId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/role`, { role });
      swap(data); setOpenId(null);
      toast.success(role === "admin" ? `${who(u)} is now an admin. They need to refresh the site to see Admin.` : `${who(u)} is no longer an admin`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change admin access"); }
    finally { setBusyId(null); }
  };

  // Lock an account out. Its email stays on file, so the same person cannot just sign up again.
  const ban = async (u) => {
    const ask = `Ban ${who(u)}? They are logged out right away and cannot log in. Their email stays reserved, so they cannot sign up again with it. You can unban them at any time.${u.pays_stripe ? " They pay through Stripe, and a ban does not stop that, so cancel their subscription in Stripe too." : ""}`;
    if (!confirm(ask)) return;
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/ban`);
      swap(data); setOpenId(null);
      toast.success(`${who(u)} is banned`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not ban this account"); }
    finally { setBusyId(null); }
  };

  const unban = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.delete(`/admin/users/${u.id}/ban`);
      swap(data);
      toast.success(`${who(u)} can log in again`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not unban this account"); }
    finally { setBusyId(null); }
  };

  // Erase an account for good. Owner only (the server checks too).
  const erase = async (u) => {
    if (!confirm(`Delete ${who(u)} for good? Their account, playlists, favorites and play history are erased and cannot be brought back. They could sign up again with the same email. To keep someone out, use Ban instead.`)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((list) => list.filter((x) => x.id !== u.id)); setOpenId(null); setStatsKey((k) => k + 1);
      toast.success(`${who(u)} was deleted`);
      if (onChange) onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete this account"); }
    finally { setBusyId(null); }
  };

  // For a real person whose confirmation email never arrived.
  const confirmEmail = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/verify`);
      swap(data); setStatsKey((k) => k + 1);
      toast.success(`${who(u)}'s email is marked as confirmed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm this email"); }
    finally { setBusyId(null); }
  };

  const remove = async (u) => {
    if (!confirm(`Remove Premium from ${who(u)}? Their account goes back to free right away.`)) return;
    setBusyId(u.id);
    try {
      const { data } = await api.delete(`/admin/users/${u.id}/comp`);
      swap(data);
      toast.success(`${who(u)} is back on a free account`);
      if (onChange) onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change Premium"); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-user-manager">
      <h2 className="font-display text-2xl font-bold mb-4">Users</h2>
      <div className="bg-zinc-900/60 border border-white/10 rounded-2xl p-4 sm:p-6">
        <SignupWatch version={statsKey} onPurged={() => { setStatsKey((k) => k + 1); setReloadKey((k) => k + 1); if (onChange) onChange(); }} />
        <label htmlFor="admin-user-search" className="sr-only">Search users by name or email</label>
        <input id="admin-user-search" value={q} onChange={(e) => { setQ(e.target.value); setOpenId(null); }} placeholder="Search by name or email" data-testid="admin-user-search"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-cyan-400/50 mb-2" />
        <p className="text-xs text-zinc-500 mb-2">Upgrade a free account to complimentary Premium for family, promotions or events. Trials and free months switch off by themselves. Ban locks an account out but keeps its email reserved, and you can unban it. Delete erases an account for good. As the site owner you can also make someone an admin, and remove that again.</p>
        <div className="divide-y divide-white/10" data-testid="admin-user-list">
          {users.map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.email}`} className="py-3">
             <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{who(u)}</div>
                <div className="text-xs text-zinc-500 truncate">{u.email}{u.created_at ? ` · joined ${fmtDay(u.created_at)}` : ""}</div>
                <div className="text-xs text-zinc-500 truncate" data-testid={`admin-user-visit-${u.email}`}>{visitLine(u)}</div>
              </div>
              <PlanBadge u={u} />
              {u.email_verified === false && (
                <span data-testid={`admin-user-unconfirmed-${u.email}`} className="inline-block self-start sm:self-auto px-2.5 py-1 rounded-full border border-amber-400/40 text-amber-300 text-xs font-mono-ui">Email not confirmed</span>
              )}
              <div className="flex flex-col gap-2 sm:w-72 sm:items-end">
                <button type="button" aria-expanded={playsId === u.id} data-testid={`admin-user-plays-${u.email}`} onClick={() => setPlaysId(playsId === u.id ? null : u.id)}
                  className="w-full sm:w-auto px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 text-sm">{playsId === u.id ? "Hide what they played" : "What they played"}</button>
                {u.plan === "free" && openId !== u.id && (
                  <button type="button" disabled={busyId === u.id} aria-expanded="false" data-testid={`admin-user-upgrade-${u.email}`} onClick={() => setOpenId(u.id)}
                    className="w-full sm:w-auto px-3 py-2 rounded-lg bg-cyan-400 text-black text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
                    <Sparkles className="w-3.5 h-3.5" /> Upgrade to complimentary Premium
                  </button>
                )}
                {(u.plan === "free" || u.premium_comp === "trial") && !u.banned && openId !== u.id && (
                  <button type="button" disabled={busyId === u.id} data-testid={`admin-user-freeweek-${u.email}`} onClick={() => giveWeek(u)}
                    className="w-full sm:w-auto px-3 py-2 rounded-lg border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 text-sm flex items-center justify-center gap-1.5 disabled:opacity-50">
                    {u.premium_comp === "trial" ? "Add another free week and email them" : "Give a free week and email them"}
                  </button>
                )}
                {(u.plan === "free" || u.premium_comp === "trial") && u.free_week_at && (
                  <p data-testid={`admin-user-freeweek-done-${u.email}`} className="text-xs text-zinc-500 sm:text-right">Last free week given {fmtDay(u.free_week_at)}{u.free_week_count > 1 ? ` (${u.free_week_count} so far)` : ""}</p>
                )}
                {u.plan === "free" && openId === u.id && (
                  <div className="w-full sm:w-72 space-y-2" data-testid={`admin-user-choices-${u.email}`}>
                    {COMP_CHOICES.map((c) => (
                      <button key={c.kind} type="button" disabled={busyId === u.id} data-testid={`admin-user-comp-${c.kind}-${u.email}`} onClick={() => give(u, c.kind)}
                        className="w-full text-left px-3 py-2 rounded-lg border border-cyan-400/40 hover:bg-cyan-400/10 disabled:opacity-50">
                        <span className="block text-sm font-semibold">{c.label}</span>
                        <span className="block text-xs text-zinc-400">{c.note}</span>
                      </button>
                    ))}
                    <button type="button" data-testid={`admin-user-choices-cancel-${u.email}`} onClick={() => setOpenId(null)}
                      className="w-full px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/5">Cancel</button>
                  </div>
                )}
                {u.can_change_role && u.role !== "admin" && openId !== u.id && (
                  <button type="button" disabled={busyId === u.id} data-testid={`admin-user-promote-${u.email}`} onClick={() => setRole(u, "admin")}
                    className="w-full sm:w-auto px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 text-sm disabled:opacity-50">Make admin</button>
                )}
                {u.can_change_role && u.role === "admin" && (
                  <button type="button" disabled={busyId === u.id} data-testid={`admin-user-demote-${u.email}`} onClick={() => setRole(u, "user")}
                    className="w-full sm:w-auto px-3 py-2 rounded-lg border border-crimson/50 text-crimson hover:bg-crimson/10 text-sm disabled:opacity-50">Remove admin</button>
                )}
                {["complimentary", "trial", "month", "premium"].includes(u.plan) && (
                  <button type="button" disabled={busyId === u.id} data-testid={`admin-user-remove-${u.email}`} onClick={() => remove(u)}
                    className="w-full sm:w-auto px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 text-sm disabled:opacity-50">
                    {u.plan === "premium" ? "Remove Premium" : "Remove complimentary Premium"}
                  </button>
                )}
                {u.banned && u.pays_stripe && (
                  <p data-testid={`admin-user-billing-${u.email}`} className="text-xs text-amber-300 sm:text-right">Still billed by Stripe. Cancel their subscription in Stripe.</p>
                )}
                {u.email_verified === false && (
                  <button type="button" disabled={busyId === u.id} data-testid={`admin-user-confirm-${u.email}`} onClick={() => confirmEmail(u)}
                    className="w-full sm:w-auto px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 text-sm disabled:opacity-50">Mark email as confirmed</button>
                )}
                {(u.can_ban || u.can_delete) && (
                  <div className="flex gap-2 w-full sm:w-auto sm:justify-end">
                    {u.can_ban && !u.banned && (
                      <button type="button" disabled={busyId === u.id} data-testid={`admin-user-ban-${u.email}`} onClick={() => ban(u)}
                        className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 text-sm disabled:opacity-50">Ban</button>
                    )}
                    {u.can_ban && u.banned && (
                      <button type="button" disabled={busyId === u.id} data-testid={`admin-user-unban-${u.email}`} onClick={() => unban(u)}
                        className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 text-sm disabled:opacity-50">Unban</button>
                    )}
                    {u.can_delete && (
                      <button type="button" disabled={busyId === u.id} data-testid={`admin-user-delete-${u.email}`} onClick={() => erase(u)}
                        className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg border border-crimson/50 text-crimson hover:bg-crimson/10 text-sm disabled:opacity-50">Delete account</button>
                    )}
                  </div>
                )}
              </div>
             </div>
             {playsId === u.id && (
               <div className="mt-3 rounded-xl border border-white/10 bg-black/20 p-3" data-testid={`admin-user-plays-panel-${u.email}`}>
                 <UserPlays userId={u.id} />
               </div>
             )}
            </div>
          ))}
          {loaded && users.length === 0 && (
            <div className="text-zinc-500 text-sm py-6 text-center" data-testid="admin-user-empty">{q.trim() ? `No one matches “${q.trim()}”.` : "No users yet."}</div>
          )}
        </div>
        {users.length >= USER_LIMIT && <p className="text-xs text-zinc-500 mt-3">Showing the newest {USER_LIMIT}. Type a name or email to find someone else.</p>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mood management sub-panel: add / delete
// ---------------------------------------------------------------------------
function MoodManager({ moods, onChange, onRenamed }) {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [lightsId, setLightsId] = useState(null);
  const [lightsDraft, setLightsDraft] = useState(normScheme({ style: "off" }));
  const [savingLights, setSavingLights] = useState(false);
  const lightsFor = moods.find((x) => x.id === lightsId) || null;

  const openLights = (m) => {
    if (lightsId === m.id) { setLightsId(null); return; }
    setLightsId(m.id); setLightsDraft(normScheme(m.lights || { style: "off" }));
  };
  // Save (lights = the editor's choice) or go back to the starter look (lights = null).
  const saveLights = async (m, lights) => {
    setSavingLights(true);
    try {
      const { data } = await api.put(`/admin/moods/${m.id}/lights`, { lights });
      if (lights === null) setLightsDraft(normScheme(data.lights || { style: "off" }));
      toast.success(lights === null ? `"${m.name}" is back to its starter lights` : `Lights saved for "${m.name}"`);
      window.dispatchEvent(new Event("dtm-moods-changed"));
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingLights(false); }
  };

  const startRename = (m) => { setEditingId(m.id); setDraft(m.name); };
  const cancelRename = () => { setEditingId(null); setDraft(""); };

  // Fix a mood's spelling. Every song that has it is updated too. If the new
  // spelling is already on the list, ask first, then combine the two.
  const saveRename = async (m) => {
    const name = draft.trim().replace(/\s+/g, " ");
    if (!name) { toast.error("Type a mood name first."); return; }
    if (name === m.name) { cancelRename(); return; }
    let merge = false;
    const clash = moods.find((x) => x.id !== m.id && x.name.toLowerCase() === name.toLowerCase());
    if (clash) {
      const n = m.song_count;
      const songs = n === 1 ? "The 1 song" : `The ${n} songs`;
      if (!confirm(`"${clash.name}" is already on the list. Combine them? ${n ? `${songs} with "${m.name}" will get "${clash.name}" instead, and ` : ""}"${m.name}" will be removed from the list.`)) return;
      merge = true;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/moods/${m.id}`, { name, merge });
      const n = data.songs_updated;
      toast.success(merge ? `Combined into "${data.name}"` : `Renamed to "${data.name}"${n ? ` (${n} song${n === 1 ? "" : "s"} updated)` : ""}`);
      if (data.name !== m.name && onRenamed) onRenamed(m.name, data.name);
      cancelRename(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    const name = newName.trim(); if (!name) return;
    try {
      await api.post("/admin/moods", { name });
      setNewName("");
      toast.success(`Mood "${name}" added`); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (m) => {
    const n = m.song_count;
    const warn = n
      ? `It is on ${n} song${n === 1 ? "" : "s"} and will be taken off ${n === 1 ? "it" : "them"} (the song${n === 1 ? "" : "s"} stay).`
      : "It is not on any songs.";
    if (!confirm(`Delete the mood "${m.name}"? ${warn}`)) return;
    try {
      await api.delete(`/admin/moods/${m.id}`);
      toast.success(`Mood "${m.name}" deleted`); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-mood-manager">
      <h2 className="font-display text-2xl font-bold mb-4">Manage moods ({moods.length})</h2>
      <form onSubmit={create} className="bg-zinc-900/60 border border-white/10 rounded-lg p-3 mb-4" data-testid="admin-new-mood-form">
        <div className="flex gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="One mood at a time (e.g. Late Night)" maxLength={30} data-testid="admin-new-mood-input"
            className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50" />
          <button data-testid="admin-add-mood" className="shrink-0 px-3 py-2 rounded-lg bg-cyan-400 text-black text-sm font-semibold flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
      </form>
      <div className="flex flex-wrap gap-2" data-testid="admin-mood-list">
        {moods.map((m) => (editingId === m.id ? (
          <div key={m.id} className="flex items-center gap-1 pl-2 pr-1 py-1 bg-zinc-900/60 border border-cyan-400/50 rounded-full text-sm" data-testid={`admin-mood-${m.name}`}>
            <input autoFocus value={draft} maxLength={30} onChange={(e) => setDraft(e.target.value)} aria-label={`New spelling for ${m.name}`}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveRename(m); } else if (e.key === "Escape") cancelRename(); }}
              data-testid={`admin-mood-rename-input-${m.name}`}
              className="w-36 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-base sm:text-sm focus:outline-none focus:border-cyan-400/50" />
            <button type="button" disabled={saving} data-testid={`admin-mood-rename-save-${m.name}`} onClick={() => saveRename(m)} title="Save"
              className="p-1.5 rounded-full bg-cyan-400 text-black disabled:opacity-50"><Check className="w-3.5 h-3.5" /></button>
            <button type="button" data-testid={`admin-mood-rename-cancel-${m.name}`} onClick={cancelRename} title="Cancel"
              className="p-1.5 rounded-full hover:bg-white/10 text-zinc-400"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <div key={m.id} className="flex items-center gap-1 pl-3 pr-1 py-1 bg-zinc-900/60 border border-white/10 rounded-full text-sm" data-testid={`admin-mood-${m.name}`}>
            <span>{m.name}</span>
            <span className="text-[10px] text-zinc-500 font-mono-ui">{m.song_count}</span>
            <button type="button" data-testid={`admin-mood-lights-${m.name}`} onClick={() => openLights(m)} title={`Lights for "${m.name}"`} aria-pressed={lightsId === m.id}
              className={`flex items-center gap-1 pl-1.5 pr-2 py-1 rounded-full text-xs hover:bg-white/10 ${lightsId === m.id ? "bg-white/10 text-white" : "text-zinc-400"}`}>
              <SchemeDot scheme={m.lights || { style: "off" }} size={12} /><Lightbulb className="w-3.5 h-3.5" />
            </button>
            <button type="button" data-testid={`admin-mood-edit-${m.name}`} onClick={() => startRename(m)} title={`Rename "${m.name}"`}
              className="p-1.5 rounded-full hover:bg-white/10 text-zinc-400 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
            <button type="button" data-testid={`admin-mood-delete-${m.name}`} onClick={() => del(m)} title={`Delete "${m.name}"`}
              className="p-1.5 rounded-full hover:bg-crimson/10 text-crimson"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )))}
        {moods.length === 0 && <div className="text-zinc-500 text-sm py-4">No moods yet.</div>}
      </div>
      {lightsFor && (
        <div className="mt-4 bg-zinc-900/60 border border-white/10 rounded-lg p-4 max-w-lg" data-testid="admin-mood-lights-panel">
          <div className="flex items-center justify-between gap-3 mb-1">
            <h3 className="font-display text-lg font-bold">Lights for &ldquo;{lightsFor.name}&rdquo;</h3>
            <button type="button" data-testid="admin-mood-lights-close" onClick={() => setLightsId(null)} className="px-3 py-1.5 rounded-full border border-white/15 text-sm hover:bg-white/5">Close</button>
          </div>
          <p className="text-xs text-zinc-500" data-testid="admin-mood-lights-note">
            {lightsFor.lights_set
              ? "These lights are saved. Songs whose primary mood is this one show them."
              : lightsFor.lights
                ? "These are the starter lights for this mood. Press Save lights to make them yours."
                : "No lights yet, so songs with this primary mood show the standard look. Pick a style to give it some."}
          </p>
          <LightsEditor value={lightsDraft} onChange={setLightsDraft} allowOff testId="admin-mood-lights-editor" />
          <div className="flex flex-wrap gap-2 mt-4">
            <button type="button" disabled={savingLights} data-testid="admin-mood-lights-save" onClick={() => saveLights(lightsFor, lightsDraft)}
              className="px-4 py-2 rounded-lg bg-cyan-400 text-black text-sm font-semibold disabled:opacity-50">Save lights</button>
            {lightsFor.lights_set && (
              <button type="button" disabled={savingLights} data-testid="admin-mood-lights-reset" onClick={() => saveLights(lightsFor, null)}
                className="px-4 py-2 rounded-lg border border-white/15 text-sm hover:bg-white/5 disabled:opacity-50">Back to the starter look</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Genre management sub-panel: add / rename / delete
// ---------------------------------------------------------------------------
function GenreManager({ genres, onChange }) {
  const [newName, setNewName] = useState("");
  const [newArt, setNewArt] = useState({ autoArt: true, artImage: "" });
  const [editingSlug, setEditingSlug] = useState(null);
  const [draft, setDraft] = useState({ name: "", slug: "" });

  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const create = async (e) => {
    e.preventDefault();
    const name = newName.trim(); if (!name) return;
    try {
      await api.post("/admin/genres", {
        slug: slugify(name),
        name,
        autoArt: newArt.autoArt,
        artImage: newArt.artImage || null,
      });
      setNewName(""); setNewArt({ autoArt: true, artImage: "" });
      toast.success("Genre added"); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startRename = (g) => { setEditingSlug(g.slug); setDraft({ name: g.name, slug: g.slug }); };
  const cancelRename = () => { setEditingSlug(null); setDraft({ name: "", slug: "" }); };

  const saveRename = async (originalSlug) => {
    const name = draft.name.trim().replace(/\s+/g, " ");
    if (!name) { toast.error("Type a genre name first."); return; }
    try {
      await api.patch(`/admin/genres/${originalSlug}`, { name, slug: slugify(draft.slug || name) });
      toast.success("Genre updated"); cancelRename(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const patchArt = async (slug, partial) => {
    try {
      await api.patch(`/admin/genres/${slug}`, partial);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update art"); }
  };

  const del = async (g) => {
    if (!confirm(`Delete genre "${g.name}"? It will be removed from any tagged videos.`)) return;
    try {
      await api.delete(`/admin/genres/${g.slug}`);
      toast.success("Genre deleted"); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-genre-manager">
      <h2 className="font-display text-2xl font-bold mb-4">Manage genres ({genres.length})</h2>
      <form onSubmit={create} className="bg-zinc-900/60 border border-white/10 rounded-lg p-3 mb-4 space-y-2" data-testid="admin-new-genre-form">
        <div className="flex gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New genre name (e.g. Lo-fi Beats)" data-testid="admin-new-genre-input"
            className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50" />
          <button data-testid="admin-add-genre" className="shrink-0 px-3 py-2 rounded-lg bg-cyan-400 text-black text-sm font-semibold flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <GenreArtControls
          value={{ autoArt: newArt.autoArt, artImage: newArt.artImage, image: newArt.artImage || null }}
          onChange={(partial) => setNewArt((s) => ({ ...s, ...partial }))}
          testIdPrefix="new-genre"
        />
      </form>
      <div className="space-y-2 max-h-[480px] overflow-y-auto pr-2">
        {genres.map((g) => (
          <div key={g.slug} className="p-2 bg-zinc-900/60 border border-white/10 rounded-lg space-y-2" data-testid={`admin-genre-${g.slug}`}>
            <div className="flex items-center gap-2">
              {editingSlug === g.slug ? (
                <>
                  <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name" data-testid={`admin-genre-name-input-${g.slug}`}
                    className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:border-cyan-400/50" />
                  <input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} placeholder="slug" data-testid={`admin-genre-slug-input-${g.slug}`}
                    className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs font-mono-ui focus:outline-none focus:border-cyan-400/50" />
                  <button data-testid={`admin-genre-save-${g.slug}`} onClick={() => saveRename(g.slug)} className="p-1.5 rounded-full bg-cyan-400 text-black"><Check className="w-3.5 h-3.5" /></button>
                  <button onClick={cancelRename} className="p-1.5 rounded-full hover:bg-white/10 text-zinc-400"><X className="w-3.5 h-3.5" /></button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{g.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono-ui truncate">{g.slug}</div>
                  </div>
                  <button data-testid={`admin-genre-edit-${g.slug}`} onClick={() => startRename(g)} className="p-1.5 rounded-full hover:bg-white/10 text-zinc-400 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`admin-genre-delete-${g.slug}`} onClick={() => del(g)} className="p-1.5 rounded-full hover:bg-crimson/10 text-crimson"><Trash2 className="w-3.5 h-3.5" /></button>
                </>
              )}
            </div>
            {editingSlug === g.slug && (
              <p className="text-[11px] text-zinc-500" data-testid={`admin-genre-rename-hint-${g.slug}`}>
                To fix spelling, change only the name. Songs stay tagged. The small code on the right is an internal label, so leave it alone unless you really need to change it.
              </p>
            )}
            <GenreArtControls
              value={{ autoArt: g.autoArt, artImage: g.artImage, image: g.image }}
              onChange={(partial) => patchArt(g.slug, partial)}
              testIdPrefix={`g-${g.slug}`}
            />
          </div>
        ))}
        {genres.length === 0 && <div className="text-zinc-500 text-sm py-4 text-center">No genres yet.</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Samples for Premium songs. A guest or a free member hears a song's sample (and nothing else) when they press a
// Premium song. This box cuts those samples (20, 25 or 30 seconds long):
//   - pick a song from the pull-down, play it in the little player, press "Start the sample here" where you want it
//     to begin (or type a time like 1:27), and press the cyan button
//   - or press "Make all ... at the best spot" and every song without a sample gets one from its loudest stretch
//   - a sample this box made shows up in the same pull-down, so it can be cut again from another spot
// Built to be easy to use on a phone: big buttons, the pull-down and the player are the phone's own.
// ---------------------------------------------------------------------------
const SAMPLE_MIN = 20;   // the shortest and longest a sample may be (the server checks the same numbers)
const SAMPLE_MAX = 30;
const fmtTime = (sec) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}`;
// "1:27" or "87" -> 87 seconds. Empty -> null (the server picks the spot). Anything else -> NaN.
const parseTime = (t) => {
  const s = (t || "").trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,3}):([0-5]?\d)$/) || s.match(/^()(\d{1,5})$/);
  if (!m) return NaN;
  return (m[1] ? parseInt(m[1], 10) * 60 : 0) + parseInt(m[2], 10);
};

// ---- The message printed on the sample's picture ("Words on the picture"). The server draws it; these helpers only show
// what it will look like. Letter sizes are in "phone pixels": the picture's short side counted as 360 pixels. A short message
// gets the biggest letters (19), a longer one smaller (down to 11 at 140 letters), and a still message that still does not
// fit on 3 lines shrinks more, but never below 5. The server uses the same numbers (words_phone_px in server.py).
const WORDS_MAX = 140;
const WORDS_PHONE_MIN = 5;
const WORDS_PHONE_MAX = 19;
const WORDS_FALLBACK_COLOR = "#22d3ee";
const WORDS_PREVIEW_PX = 220;                 // how tall the preview picture is on the page
const wordsPhonePx = (n) => (n <= 24 ? WORDS_PHONE_MAX : WORDS_PHONE_MAX - ((WORDS_PHONE_MAX - 11) * (Math.min(n, WORDS_MAX) - 24)) / (WORDS_MAX - 24));
// how many lines the message takes when `per` letters fit on a line (a word is only split when it is longer than a line)
const wordsLineCount = (text, per) => {
  let lines = 1;
  let cur = 0;
  text.split(" ").forEach((w) => {
    const len = w.length;
    if (len > per) { if (cur) lines += 1; lines += Math.ceil(len / per) - 1; cur = len % per || per; return; }
    if (cur === 0) cur = len;
    else if (cur + 1 + len <= per) cur += 1 + len;
    else { lines += 1; cur = len; }
  });
  return lines;
};
const wordsLetterPx = (text, ratio, slide) => {
  const base = wordsPhonePx(text.length);
  if (slide) return base;
  const usable = 0.9 * (ratio.w >= ratio.h ? (360 * ratio.w) / ratio.h : 360);
  let px = base;
  while (px > WORDS_PHONE_MIN && wordsLineCount(text, Math.max(1, Math.floor(usable / (0.6 * px)))) > 3) px -= 0.5;
  return Math.max(WORDS_PHONE_MIN, px);
};
const rgbHex = (c) => `#${c.map((v) => `0${v.toString(16)}`.slice(-2)).join("")}`;
// the colors of a song's boom lights, as #rrggbb (a black one is left out: black words would vanish)
const boomColors = (scheme) => {
  const out = [];
  if (scheme && scheme.style !== "off") {
    for (let i = 0; i < scheme.colors; i += 1) {
      const c = schemeColor(scheme, i);
      if (c[0] * 0.3 + c[1] * 0.59 + c[2] * 0.11 < 40) continue;
      const hex = rgbHex(c);
      if (!out.includes(hex)) out.push(hex);
    }
  }
  return out;
};
const placeWords = (places) => {
  const names = ["top", "middle", "bottom"].filter((p) => places.includes(p));
  return names.length === 3 ? "at the top, middle and bottom" : `at the ${names.join(" and ")}`;
};

// Can the page listen to this video's sound (so the Beat boom follows the music)? A video from another web address only
// allows that when it is loaded with the crossorigin attribute, and only browsers that can copy the sound (not an iPhone).
const canListen = () => typeof HTMLMediaElement !== "undefined" && typeof HTMLMediaElement.prototype.captureStream === "function";
const otherSite = (url) => { try { return new URL(url, window.location.href).origin !== window.location.origin; } catch (e) { return false; } };
const STAGE_PICTURE_PX = 320;   // how tall the picture may be in the preview
// What the Beat lights can hear (info comes from Backlight's onInfo; null when the lights are not on Beat)
function beatNote(info) {
  if (!info) return "";
  if (info.status === "music") return "The glow is following the beat.";
  if (info.status === "waiting") return "Listening to the song…";
  if (info.status === "blocked") return "It can not hear this video's sound from here, so the glow pulses at a steady tempo.";
  if (info.status === "unsupported") return "This browser can not let the page listen to the music (an iPhone can not), so the glow pulses at a steady tempo.";
  return "Press play and the glow follows the beat.";
}

// A pull-down with a search box as its first line: type a few letters of the title (or the artist) and the list narrows.
// Enter picks the first one in the list, the arrow keys move up and down, Escape closes it.
function SongPicker({ missing, made, pickedId, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const box = useRef(null);
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (m) => { const hay = `${m.title || ""} ${m.artist || ""}`.toLowerCase(); return words.every((w) => hay.includes(w)); };
  const shownMissing = missing.filter(hit);
  const shownMade = made.filter(hit);
  const flat = [...shownMissing, ...shownMade];
  const cur = Math.min(hi, Math.max(0, flat.length - 1));
  const picked = missing.find((m) => m.id === pickedId) || made.find((m) => m.id === pickedId) || null;

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("touchstart", away);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("touchstart", away); };
  }, [open]);
  useEffect(() => {
    if (!open || !box.current) return;
    const el = box.current.querySelector('[data-hi="1"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [open, cur, q]);

  const close = () => { setOpen(false); setQ(""); setHi(0); };
  const choose = (m) => { onPick(m.id); close(); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi(Math.min(flat.length - 1, cur + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(Math.max(0, cur - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (flat[cur]) choose(flat[cur]); }
    else if (e.key === "Escape") { e.preventDefault(); close(); }
  };
  const row = (m, isMade) => {
    const idx = flat.indexOf(m);
    const on = idx === cur;
    return (
      <button key={m.id} type="button" role="option" aria-selected={m.id === pickedId} data-hi={on ? "1" : undefined} data-testid={`admin-samples-option-${m.id}`}
        onClick={() => choose(m)} onMouseEnter={() => setHi(idx)}
        className={`w-full min-h-[44px] text-left px-3 py-1.5 rounded-md ${on ? "bg-cyan-400/15" : ""} ${m.id === pickedId ? "text-cyan-300" : "text-white"}`}>
        <span className="block text-base leading-tight">{m.title}</span>
        <span className="block text-xs text-zinc-400 leading-tight">{isMade ? `starts ${fmtTime(m.start || 0)} · ${m.seconds} sec` : (m.artist || "")}{isMade && m.artist ? ` · ${m.artist}` : ""}</span>
      </button>
    );
  };

  return (
    <div ref={box} className="relative" data-testid="admin-samples-picklist">
      <button type="button" onClick={() => (open ? close() : setOpen(true))} aria-haspopup="listbox" aria-expanded={open} data-testid="admin-samples-pick"
        className="w-full min-h-[48px] flex items-center justify-between gap-2 bg-zinc-900 border border-white/15 rounded-lg px-3 text-base text-left text-white focus:outline-none focus:border-cyan-400/60">
        <span className={`min-w-0 truncate ${picked ? "" : "text-zinc-400"}`}>{picked ? picked.title : "Choose a song…"}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 rounded-lg border border-white/15 bg-zinc-900 p-2 space-y-2" data-testid="admin-samples-dropdown">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setHi(0); }} onKeyDown={onKey} placeholder="Search for a song…" aria-label="Search for a song" data-testid="admin-samples-search"
              className="w-full min-h-[44px] pl-9 pr-3 text-base bg-white/5 border border-white/15 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:border-cyan-400/60" />
          </div>
          <div role="listbox" className="max-h-72 overflow-y-auto pr-1 space-y-1" data-testid="admin-samples-options">
            {shownMissing.length > 0 && (
              <div data-testid="admin-samples-group-none">
                <div className="px-3 pt-1 pb-0.5 text-[11px] font-mono-ui uppercase tracking-widest text-zinc-500">No sample yet ({shownMissing.length})</div>
                {shownMissing.map((m) => row(m, false))}
              </div>
            )}
            {shownMade.length > 0 && (
              <div data-testid="admin-samples-group-made">
                <div className="px-3 pt-1 pb-0.5 text-[11px] font-mono-ui uppercase tracking-widest text-zinc-500">Samples made here ({shownMade.length})</div>
                {shownMade.map((m) => row(m, true))}
              </div>
            )}
            {flat.length === 0 && <div className="px-3 py-3 text-sm text-zinc-400" data-testid="admin-samples-nomatch">No song matches “{q.trim()}”. Try fewer letters.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function SampleMaker({ onChange, videos = [], moods = [] }) {
  const [info, setInfo] = useState(null);       // what the server says: the songs without a sample, the ones it made, the last job
  const [failed, setFailed] = useState(false);
  const [starting, setStarting] = useState(false);
  const [seconds, setSeconds] = useState(20);   // how long the next sample is
  const [pickId, setPickId] = useState("");     // the song chosen in the pull-down
  const [startText, setStartText] = useState(""); // where its sample starts (empty = the best spot)
  const wasRunning = useRef(false);
  const vref = useRef(null);
  const sref = useRef(null);                  // the little player for the sample this box made
  const [playing, setPlaying] = useState(false);  // the player is playing (the lights brighten and follow the beat while it does)
  const [ratio, setRatio] = useState({ w: 16, h: 9 }); // the shape of the picked song's picture
  const [beatInfo, setBeatInfo] = useState(null);   // what the beat lights can hear
  const [noListen, setNoListen] = useState({});     // song id -> true when its video address refused to let the page listen
  const [hearing, setHearing] = useState(false);  // the player is playing just the picked stretch
  const hearEnd = useRef(0);                      // where that stretch ends, in seconds
  // the saved message printed on the picture, where it goes, and whether it slides (all saved on the server)
  const [wSaved, setWSaved] = useState(null);      // {text, places, motion} as saved
  const [wDraft, setWDraft] = useState("");        // what is typed in the box
  const [wEdit, setWEdit] = useState(false);       // the box is open (first time, or the pencil was pressed)
  const [wBusy, setWBusy] = useState(false);
  const [places, setPlaces] = useState([]);        // "top", "middle", "bottom"; none picked = bottom
  const [motion, setMotion] = useState("still");   // "still" or "slide"
  const [colorIdx, setColorIdx] = useState(0);     // which of the song's boom colors the words use
  const hydrated = useRef(false);
  const [autoBusy, setAutoBusy] = useState(false);   // the Automatic samples switch is being changed

  const check = async () => {
    try { const r = await api.get("/admin/samples"); setInfo(r.data); setFailed(false); }
    catch (e) { setFailed(true); }
  };
  useEffect(() => { check(); }, []);

  // the first time the server answers, fill in the saved message (nothing saved yet: the box opens and waits for the first message)
  useEffect(() => {
    if (!info || hydrated.current) return;
    hydrated.current = true;
    const w = info.words || {};
    const text = w.text || "";
    setWSaved({ text, places: w.places || [], motion: w.motion === "slide" ? "slide" : "still" });
    setWDraft(text);
    setPlaces(w.places || []);
    setMotion(w.motion === "slide" ? "slide" : "still");
    setWEdit(!text);
  }, [info]);

  const job = info?.job || null;
  const running = job?.state === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(check, 4000);
    return () => clearInterval(t);
  }, [running]);
  // when a run finishes, reload the song list so the songs show their new sample
  useEffect(() => {
    if (wasRunning.current && !running) onChange();
    wasRunning.current = running;
  }, [running]);

  // "Hear these seconds first": play only the picked stretch in the player, then stop by itself
  useEffect(() => {
    if (!hearing) return undefined;
    const t = setInterval(() => {
      const v = vref.current;
      if (!v) { setHearing(false); return; }
      if (v.paused || v.ended) { setHearing(false); return; }
      if (v.currentTime >= hearEnd.current) { v.pause(); setHearing(false); }
    }, 100);
    return () => clearInterval(t);
  }, [hearing]);

  const missing = info?.missing || [];
  const made = info?.made || [];
  const picked = missing.find((m) => m.id === pickId) || made.find((m) => m.id === pickId) || null;
  const isMade = !!picked && made.some((m) => m.id === pickId);
  const startNum = parseTime(startText);
  const startBad = Number.isNaN(startNum);
  // while a stretch is playing, the slider and the start box move where it ends
  useEffect(() => { if (hearing && startNum !== null && !startBad) hearEnd.current = startNum + seconds; }, [hearing, startNum, startBad, seconds]);
  // the lights a visitor sees with this song: the lights of its main mood (set under Manage moods)
  const moodMap = useMemo(() => { const m = {}; (moods || []).forEach((x) => { m[x.name] = x.lights || null; }); return m; }, [moods]);
  const song = (videos || []).find((v) => v.id === pickId) || null;
  const lights = songLights(song, moodMap);
  const lit = lights && lights.style !== "off" ? lights : null;
  const moodName = songMoodName(song);
  // the colors the words can take: the song's boom colors, then white (a song with no lights gets the site's cyan and white)
  const dotsFor = (sid) => {
    const l = songLights((videos || []).find((v) => v.id === sid) || null, moodMap);
    const c = boomColors(l && l.style !== "off" ? l : null);
    if (!c.includes("#ffffff")) c.push("#ffffff");
    return c.length > 1 ? c : [WORDS_FALLBACK_COLOR, "#ffffff"];
  };
  const dots = dotsFor(pickId);
  const colorHex = dots[Math.min(colorIdx, dots.length - 1)];
  const cleanWords = (t) => t.replace(/\s+/g, " ").trim().slice(0, WORDS_MAX);
  const wordsNow = wEdit ? cleanWords(wDraft) : (wSaved?.text || "");   // the words the next cut will print ("" = none)
  const effPlaces = places.length ? places : ["bottom"];
  const slide = motion === "slide";
  const listenable = !!picked && !!picked.video_url && canListen() && otherSite(picked.video_url) && !noListen[picked.id];
  // the video address refused to be listened to: load it again without asking (it plays, the boom just can not hear it)
  const onVideoError = (e) => {
    const v = e.currentTarget;
    if (!v || !picked || !v.hasAttribute("crossorigin") || v.readyState > 0) return;
    setNoListen((n) => ({ ...n, [picked.id]: true }));
    v.removeAttribute("crossorigin");
    v.load();
  };
  const onVideoMeta = (e) => {
    const v = e.currentTarget;
    if (v && v.videoWidth && v.videoHeight) setRatio({ w: v.videoWidth, h: v.videoHeight });
  };

  // Where the words go and still / sliding are saved the moment they change. The message itself is saved with "Save words".
  const saveLook = async (nextPlaces, nextMotion) => {
    try {
      await api.put("/admin/samples/words", { words: wSaved?.text || "", places: nextPlaces, motion: nextMotion });
      setWSaved((w) => ({ ...(w || { text: "" }), places: nextPlaces, motion: nextMotion }));
    } catch (e) { /* what is picked is still used for the next cut, it just will not be remembered */ }
  };
  const togglePlace = (p) => { const next = effPlaces.includes(p) ? effPlaces.filter((x) => x !== p) : [...effPlaces, p]; setPlaces(next); saveLook(next, motion); };
  const toggleAllPlaces = () => { const next = effPlaces.length === 3 ? [] : ["top", "middle", "bottom"]; setPlaces(next); saveLook(next, motion); };
  const pickMotion = (m) => { setMotion(m); saveLook(places, m); };
  const saveWords = async () => {
    const text = cleanWords(wDraft);
    setWBusy(true);
    try {
      const r = await api.put("/admin/samples/words", { words: text, places, motion });
      setWSaved({ text: r.data.text, places: r.data.places, motion: r.data.motion });
      setWDraft(r.data.text);
      setWEdit(!r.data.text);
      toast.success(r.data.text ? "Message saved" : "Message cleared. New samples will have no words.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the message. Try again in a minute."); }
    finally { setWBusy(false); }
  };
  const cancelWords = () => { setWDraft(wSaved?.text || ""); setWEdit(!(wSaved?.text)); };
  // typed but not saved yet, and a cut is starting: save it first, so what is cut is what is saved
  const ensureSaved = async () => { if (wEdit && wordsNow !== (wSaved?.text || "")) await saveWords(); };
  const wordsPayload = (color, colors) => ({ words: wordsNow, places, motion, color, ...(colors ? { colors } : {}) });

  // keep = stay on the same song afterwards, so its new sample is right there to play
  const send = async (payload, keep = false) => {
    setStarting(true);
    try {
      const r = await api.post("/admin/samples/start", payload);
      if (!r.data.started) toast.success("Nothing to cut right now.");
      if (!keep) { setPickId(""); setStartText(""); }
      await check();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start. Try again in a minute."); }
    finally { setStarting(false); }
  };

  const choose = (id) => {
    setPickId(id);
    // a song that already has a sample comes back with that sample's start and length filled in, so cutting it again
    // (with a new message, say) keeps the same spot. "Best spot instead" still empties the start.
    const m = made.find((x) => x.id === id);
    setStartText(m && Number.isFinite(m.start) ? fmtTime(m.start) : "");
    if (m && Number.isFinite(m.seconds)) setSeconds(Math.min(SAMPLE_MAX, Math.max(SAMPLE_MIN, Math.round(m.seconds))));
    // ...and its words come back the way they were printed (where, still or sliding, color); a song with nothing remembered uses the saved choice
    const lk = m && m.look;
    setPlaces(lk ? lk.places : (wSaved?.places || []));
    setMotion(lk ? lk.motion : (wSaved?.motion === "slide" ? "slide" : "still"));
    const ci = lk ? dotsFor(id).indexOf(lk.color) : -1;
    setColorIdx(ci >= 0 ? ci : 0);
    setHearing(false); setPlaying(false); setRatio({ w: 16, h: 9 }); setBeatInfo(null);
  };
  const hearFirst = () => {
    const v = vref.current;
    if (!v) return;
    if (hearing) { v.pause(); setHearing(false); return; }
    if (startNum === null || startBad) { toast.error("There is no start time yet. Press Start the sample here (or type a time like 1:27), then press this."); return; }
    if (Number.isFinite(v.duration) && startNum >= v.duration) { toast.error("That start is past the end of the song."); return; }
    hearEnd.current = startNum + seconds;
    v.currentTime = startNum;
    setHearing(true);
    const p = v.play();
    if (p && p.catch) p.catch(() => { setHearing(false); toast.error("The player would not start. Press its play button once, then try again."); });
  };
  const useHere = () => {
    if (!vref.current) { toast.error("Press play on the song first, then press this where you want the sample to begin."); return; }
    setStartText(fmtTime(vref.current.currentTime || 0));
  };
  const makeOne = async () => {
    if (!picked || startBad) return;
    await ensureSaved();
    await send({ seconds, only: [picked.id], redo: isMade, starts: startNum === null ? {} : { [picked.id]: startNum }, ...wordsPayload(colorHex) }, true);
  };
  const makeAll = async () => {
    if (!confirm(`Cut a ${seconds}-second sample for ${missing.length} Premium song${missing.length === 1 ? "" : "s"}, each from its loudest stretch in the middle${wordsNow ? `, with your message ${placeWords(effPlaces)}` : ""}? It runs in the background and takes a few minutes. Songs that already have a sample are left alone.`)) return;
    // each song's words take that song's own first boom color
    const colors = {};
    missing.forEach((m) => { const c = boomColors(songLights((videos || []).find((v) => v.id === m.id) || null, moodMap))[0]; if (c) colors[m.id] = c; });
    await ensureSaved();
    await send({ seconds, starts: {}, ...wordsPayload(WORDS_FALLBACK_COLOR, colors) });
  };
  // Cut every sample this box made again, each where it starts now and as long as it is now, with the saved message
  // (or with no words, when the message is empty). Samples put in by hand are not in this list and are never touched.
  const updateAll = async () => {
    const n = made.length;
    if (!n) return;
    if (!confirm(`Cut ${n} sample${n === 1 ? "" : "s"} again, each at the same spot and length it has now, ${wordsNow ? "with your message, in the same color and place as its words are now" : "with no words"}? It runs in the background and you can leave the page. Visitors keep hearing the old sample until the new one is ready. Samples you put in by hand are left alone.`)) return;
    const colors = {};
    made.forEach((m) => { const c = boomColors(songLights((videos || []).find((v) => v.id === m.id) || null, moodMap))[0]; if (c) colors[m.id] = c; });
    await ensureSaved();
    // each song keeps the color, place and motion its words already have; the saved choice is only for a sample with none remembered
    await send({ seconds, redo: true, same_spot: true, starts: {}, ...wordsPayload(WORDS_FALLBACK_COLOR, colors), places: wSaved?.places || [], motion: wSaved?.motion === "slide" ? "slide" : "still" });
  };

  // Automatic samples: a Premium song saved with an empty Preview link gets a sample by itself (on until it is turned off here)
  const autoOn = !info || !info.auto || info.auto.on !== false;
  const toggleAuto = async () => {
    const next = !autoOn;
    setAutoBusy(true);
    try {
      await api.put("/admin/samples/auto", { on: next });
      toast.success(next ? (missing.length ? "Automatic samples are on. The songs that are waiting will be made in a moment." : "Automatic samples are on.") : "Automatic samples are off.");
      await check();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change that. Try again in a minute."); }
    finally { setAutoBusy(false); }
  };

  const results = job?.results || [];
  const madeCount = results.filter((r) => r.ok).length;
  const pct = running ? Math.min(100, Math.round((100 * (job.done || 0)) / Math.max(1, job.total || 1))) : 0;
  const canChoose = !!info && info.ffmpeg && !running && missing.length + made.length > 0;
  const btn = "w-full min-h-[48px] rounded-lg font-semibold text-base disabled:opacity-50";
  // how long the sample is: one slider, shown inside the song's panel (or under the song list when no song is open)
  const lengthSlider = (
    <div data-testid="admin-samples-length">
      <label htmlFor="samples-len" className="flex items-center justify-between gap-2 text-zinc-300">
        <span>Sample length</span>
        <span className="font-semibold text-cyan-300" data-testid="admin-samples-length-value">{seconds} seconds</span>
      </label>
      <input id="samples-len" type="range" min={SAMPLE_MIN} max={SAMPLE_MAX} step={1} value={seconds} onChange={(e) => setSeconds(parseInt(e.target.value, 10))}
        aria-valuetext={`${seconds} seconds`} data-testid="admin-samples-len" className="block w-full h-11 accent-cyan-400 cursor-pointer" />
      <div className="flex justify-between px-1 -mt-1 text-[11px] text-zinc-500" aria-hidden="true"><span>{SAMPLE_MIN}</span><span>25</span><span>{SAMPLE_MAX}</span></div>
    </div>
  );

  // The message printed on the picture: one saved line with a pencil to edit it, where it goes (top, middle, bottom, or all 3),
  // still or sliding, its color, and a preview over the song's cover. withPreview = inside a song's panel (the preview and the
  // colors need a song).
  const placeBtn = (id, label) => {
    const on = effPlaces.includes(id);
    return (
      <button key={id} type="button" aria-pressed={on} onClick={() => togglePlace(id)} data-testid={`admin-samples-place-${id}`}
        className={`min-h-[44px] px-3 rounded-lg border text-sm font-semibold ${on ? "bg-cyan-400/20 border-cyan-400/70 text-cyan-200" : "border-white/15 text-zinc-300"}`}>{label}</button>
    );
  };
  const motionBtn = (id, label) => (
    <button key={id} type="button" aria-pressed={motion === id} onClick={() => pickMotion(id)} data-testid={`admin-samples-motion-${id}`}
      className={`min-h-[44px] px-3 rounded-lg border text-sm font-semibold ${motion === id ? "bg-cyan-400/20 border-cyan-400/70 text-cyan-200" : "border-white/15 text-zinc-300"}`}>{label}</button>
  );
  const wordsBlock = (withPreview) => {
    const letterPx = wordsNow ? wordsLetterPx(wordsNow, ratio, slide) : 0;
    const frameW = ratio.w >= ratio.h ? (360 * ratio.w) / ratio.h : 360;                       // the picture's width in phone pixels
    const slideSecs = Math.max(4, Math.round(((1 + (wordsNow.length * 0.6 * letterPx) / frameW) / 0.16) * 10) / 10);   // crossing takes about 6 seconds, like the real cut
    const wordStyle = (scale) => ({
      fontFamily: 'ui-monospace, "DejaVu Sans Mono", Menlo, Consolas, monospace', fontWeight: 700, lineHeight: 1.28, color: colorHex,
      fontSize: `calc(${(letterPx * scale).toFixed(2)} * 100cqmin / 360)`,
      textShadow: `0 0 0.14em #000, 0 0 0.14em #000, 0 0 0.4em ${colorHex}`,
    });
    const spot = { top: { top: "3%" }, middle: { top: "50%", transform: "translateY(-50%)" }, bottom: { bottom: "3%" } };
    return (
      <div className="space-y-2" data-testid="admin-samples-words">
        <div className="flex items-center justify-between gap-2">
          <span className="text-zinc-300">Words on the picture</span>
          {!wEdit && (
            <button type="button" onClick={() => { setWDraft(wSaved?.text || ""); setWEdit(true); }} aria-label="Edit the words" title="Edit the words" data-testid="admin-samples-words-edit"
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full border border-white/15 text-zinc-300 hover:border-cyan-400/60 hover:text-cyan-300"><Pencil className="w-4 h-4" /></button>
          )}
        </div>
        {wEdit ? (
          <div className="space-y-2">
            <textarea value={wDraft} rows={2} maxLength={WORDS_MAX} onChange={(e) => setWDraft(e.target.value)} aria-label="The words on the picture" data-testid="admin-samples-words-input"
              placeholder="Type the message that goes on the samples. Leave it blank for no words."
              className="w-full text-base bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:border-cyan-400/60" />
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={saveWords} disabled={wBusy || cleanWords(wDraft) === (wSaved?.text || "")} data-testid="admin-samples-words-save"
                className="min-h-[44px] px-4 rounded-lg bg-cyan-400 text-black text-sm font-semibold disabled:opacity-50">{wBusy ? "Saving…" : "Save words"}</button>
              {wSaved?.text && <button type="button" onClick={cancelWords} data-testid="admin-samples-words-cancel" className="min-h-[44px] px-4 rounded-lg border border-white/15 text-sm text-zinc-300">Cancel</button>}
              <span className="ml-auto text-xs text-zinc-500" data-testid="admin-samples-words-count">{wDraft.length} / {WORDS_MAX}</span>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-base break-words" data-testid="admin-samples-words-saved">{wSaved?.text}</div>
        )}
        {wordsNow ? (
          <>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Where the words go" data-testid="admin-samples-places">
              {placeBtn("top", "Top")}{placeBtn("middle", "Middle")}{placeBtn("bottom", "Bottom")}
              <button type="button" aria-pressed={effPlaces.length === 3} onClick={toggleAllPlaces} data-testid="admin-samples-place-all"
                className={`min-h-[44px] px-3 rounded-lg border text-sm font-semibold ${effPlaces.length === 3 ? "bg-cyan-400/20 border-cyan-400/70 text-cyan-200" : "border-white/15 text-zinc-300"}`}>All 3</button>
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Still or sliding" data-testid="admin-samples-motions">
              {motionBtn("still", "Still")}{motionBtn("slide", "Sliding")}
            </div>
            {withPreview && (
              <div className="flex items-center gap-1 flex-wrap" data-testid="admin-samples-colors">
                <span className="text-sm text-zinc-300 mr-1">Color</span>
                {dots.map((hex, i) => (
                  <button key={hex} type="button" aria-label={`Color ${i + 1}`} aria-pressed={hex === colorHex} onClick={() => setColorIdx(i)} data-testid={`admin-samples-color-${i}`}
                    className={`w-11 h-11 rounded-full border-2 flex items-center justify-center ${hex === colorHex ? "border-white" : "border-white/20"}`}>
                    <span className="block w-6 h-6 rounded-full" style={{ background: hex, boxShadow: `0 0 8px ${hex}` }} />
                  </button>
                ))}
                <span className="text-xs text-zinc-500 ml-1">{lit ? "this song's boom colors, and white" : "this song has no lights, so these are standard colors"}</span>
              </div>
            )}
            {withPreview && (
              <div data-testid="admin-samples-words-preview">
                <style>{"@keyframes dtmWordsSlide { from { transform: translateX(100cqw); } to { transform: translateX(-100%); } }"}</style>
                <div className="relative mx-auto overflow-hidden rounded-md bg-zinc-900 ring-1 ring-white/15"
                  style={{ aspectRatio: `${ratio.w} / ${ratio.h}`, width: `min(100%, ${Math.round((WORDS_PREVIEW_PX * ratio.w) / ratio.h)}px)`, containerType: "size" }}>
                  {song?.cover_url && <img src={song.cover_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80" />}
                  {["top", "middle", "bottom"].filter((p) => effPlaces.includes(p)).map((p) => (
                    <div key={p} className="absolute left-0 right-0 overflow-hidden" style={spot[p]} data-testid={`admin-samples-words-at-${p}`}>
                      {slide
                        ? <span className="inline-block whitespace-nowrap" style={{ ...wordStyle(1), animation: `dtmWordsSlide ${slideSecs}s linear infinite` }}>{wordsNow}</span>
                        : <div className="text-center break-words" style={{ ...wordStyle(1), padding: "0 5%" }}>{wordsNow}</div>}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-1">What the sample will look like. Letters are about {Math.round(letterPx)} pixels (5 to 19: short messages get bigger letters, longer ones smaller). If none of Top, Middle or Bottom is on, the words go at the bottom.</p>
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-zinc-500" data-testid="admin-samples-words-none">No words: samples are cut with just the picture, like before.</p>
        )}
      </div>
    );
  };

  return (
    <div data-testid="admin-samples">
      <h2 className="font-display text-2xl font-bold mb-4">Samples for Premium songs</h2>
      <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-3 space-y-3 text-sm">
        <p className="text-xs text-zinc-500">
          When a guest or a free member presses a Premium song, its sample is all they hear. A Premium song with nothing in its Preview link has no sample, so they hear nothing. Pick a song below, choose where its sample starts and how long it is (20 to 30 seconds), or let me pick the loudest stretch in the middle of each song. Every sample fades in and out a little. A sample you put in by hand with the pencil is never replaced.
        </p>

        {info && info.ffmpeg && (
          <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid="admin-samples-auto">
            <button type="button" role="switch" aria-checked={autoOn} aria-label="Automatic samples" onClick={toggleAuto} disabled={autoBusy} data-testid="admin-samples-auto-switch"
              className={`relative shrink-0 w-[72px] h-11 rounded-full border transition-colors disabled:opacity-60 ${autoOn ? "bg-cyan-400/80 border-cyan-300" : "bg-white/10 border-white/25"}`}>
              <span className={`absolute top-1 left-1 h-9 w-9 rounded-full bg-white transition-transform ${autoOn ? "translate-x-7" : ""}`} />
            </button>
            <div className="min-w-0">
              <div className="font-semibold" data-testid="admin-samples-auto-label">Automatic samples: {autoOn ? "On" : "Off"}</div>
              <p className="text-xs text-zinc-500 mt-0.5">
                When you save a Premium song with an empty Preview link, a 20-second sample is cut about 20 seconds later at the best spot, with your saved message. A Preview link you paste by hand is never replaced.{autoOn ? "" : " Turning it on also makes the songs that are waiting."}
              </p>
            </div>
          </div>
        )}

        {!info && !failed && <div className="text-zinc-500" data-testid="admin-samples-loading">Checking your Premium songs…</div>}
        {failed && !info && <div className="text-amber-300" data-testid="admin-samples-failed">Could not check right now. Refresh the page and try again.</div>}

        {info && !info.ffmpeg && !running && (
          <div className="text-amber-300 border border-amber-400/30 rounded-lg p-2" data-testid="admin-samples-noffmpeg">
            The server does not have the free video tool (ffmpeg) that cuts samples yet, so I can not make them from here. Tell me and I will give you the one line that adds it.
            {missing.length > 0 && <div className="text-xs text-amber-200/80 mt-1">{missing.length} Premium song{missing.length === 1 ? " is" : "s are"} waiting for a sample: {missing.slice(0, 6).map((m) => m.title).join(", ")}{missing.length > 6 ? `, and ${missing.length - 6} more` : ""}.</div>}
          </div>
        )}

        {info && running && (
          <div data-testid="admin-samples-progress">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="min-w-0 truncate">Working on song {Math.min((job.done || 0) + 1, job.total || 1)} of {job.total}{job.current ? `: ${job.current}` : ""}</span>
              <span className="shrink-0 text-xs text-zinc-500 font-mono-ui">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-cyan-400 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
            <p className="text-xs text-zinc-500 mt-1.5">You can leave this page. It keeps going and the result will be here when you come back.</p>
          </div>
        )}

        {info && !running && missing.length === 0 && (
          <div className="flex items-center gap-1.5 text-emerald-300" data-testid="admin-samples-allset">
            <Check className="w-4 h-4 shrink-0" />
            {info.premium_total ? `All ${info.premium_total} Premium song${info.premium_total === 1 ? "" : "s"} have a sample.` : "No songs are marked Premium yet."}
          </div>
        )}

        {canChoose && (
          <div className="space-y-3" data-testid="admin-samples-chooser">
            <div>
              <span className="block text-xs text-zinc-400 mb-1">
                {missing.length > 0 ? `${missing.length} Premium song${missing.length === 1 ? " has" : "s have"} no sample yet.` : "Pick a sample to cut again."} Choose a song:
              </span>
              <SongPicker missing={missing} made={made} pickedId={pickId} onPick={choose} />
            </div>

            {picked && (
              <div className="rounded-lg border border-cyan-400/25 bg-white/[0.03] p-3 space-y-3" data-testid="admin-samples-picker">
                <div className="font-semibold">{picked.title}</div>
                {isMade && (
                  <div className="space-y-1.5" data-testid="admin-samples-made">
                    <div className="text-xs text-zinc-400">This song has a sample from this box (starts {fmtTime(picked.start || 0)}, {picked.seconds} seconds{picked.words ? `, with the words “${picked.words}”` : ""}).{picked.url ? " Play it right here:" : ""}</div>
                    {picked.url && (
                      <video key={picked.url} ref={sref} src={picked.url} controls playsInline preload="metadata" data-testid="admin-samples-hear"
                        onPlay={() => { if (vref.current) vref.current.pause(); }} className="w-full max-h-48 rounded-lg bg-black" />
                    )}
                  </div>
                )}
                {picked.video_url ? (
                  <>
                    <p className="text-xs text-zinc-400">Play the song and pause it where the sample should begin, then press the cyan button under the picture.</p>
                    <div className="relative overflow-hidden rounded-xl bg-black/50 px-4 py-8" data-testid="admin-samples-stage">
                      <div className="relative mx-auto" style={{ aspectRatio: `${ratio.w} / ${ratio.h}`, width: `min(100%, ${Math.round((STAGE_PICTURE_PX * ratio.w) / ratio.h)}px)` }} data-testid="admin-samples-frame">
                        {lit && <Backlight key={`lights-${picked.id}`} scheme={lit} paused={!playing} mediaRef={vref} room={{ x: 22, y: 30 }} onInfo={setBeatInfo} />}
                        <video key={`video-${picked.id}`} ref={vref} src={picked.video_url} crossOrigin={listenable ? "anonymous" : undefined} controls playsInline preload="metadata" data-testid="admin-samples-video"
                          onPlay={() => { setPlaying(true); if (sref.current) sref.current.pause(); }} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} onLoadedMetadata={onVideoMeta} onError={onVideoError}
                          className="relative z-[1] block h-full w-full rounded-md bg-black object-contain" />
                      </div>
                    </div>
                    <p className="text-xs text-zinc-400" data-testid="admin-samples-lights-note">
                      {lit
                        ? `Lights: ${moodName}, the same lights a visitor sees with this song. ${beatNote(beatInfo)}`
                        : (moodName ? `No lights: the mood "${moodName}" has its lights off, so a visitor sees none.` : "No lights: this song has no mood picked, so a visitor sees none. Pick a mood with the pencil in the catalog if you want the glow and the boom.")}
                    </p>
                    <button type="button" onClick={useHere} data-testid="admin-samples-usehere" className={`${btn} bg-cyan-400/15 border border-cyan-400/60 text-cyan-300`}>Start the sample here</button>
                  </>
                ) : <p className="text-xs text-amber-300">This song has no video link, so type the start time below.</p>}
                <div className="flex items-center gap-2 flex-wrap">
                  <label htmlFor="samples-startat" className="text-zinc-300">Starts at</label>
                  <input id="samples-startat" value={startText} onChange={(e) => setStartText(e.target.value)} placeholder="best spot" data-testid="admin-samples-startat"
                    className={`min-h-[44px] w-28 text-center text-base bg-white/5 border rounded-lg px-2 focus:outline-none focus:border-cyan-400/60 ${startBad ? "border-red-400/70" : "border-white/15"}`} />
                  {startText && <button type="button" onClick={() => setStartText("")} className="min-h-[44px] px-3 rounded-lg border border-white/15 text-xs text-zinc-300" data-testid="admin-samples-clear">Best spot instead</button>}
                </div>
                {lengthSlider}
                {wordsBlock(true)}
                <p className={`text-xs ${startBad ? "text-red-300" : "text-zinc-400"}`} data-testid="admin-samples-summary">
                  {startBad ? "Type the start like 1:27 (minutes, a colon, seconds), or leave it empty."
                    : startNum === null ? `I will pick the best spot. ${seconds} seconds long.`
                      : `${seconds} seconds, from ${fmtTime(startNum)} to ${fmtTime(startNum + seconds)}.`}
                  {wordsNow ? ` Your words ${placeWords(effPlaces)}, ${slide ? "sliding" : "still"}.` : ""}
                </p>
                {picked.video_url && (
                  <div>
                    <button type="button" onClick={hearFirst} disabled={startBad} data-testid="admin-samples-hearfirst" className={`${btn} border border-white/25 text-white hover:border-cyan-400/60`}>
                      {hearing ? "Stop" : `Hear these ${seconds} seconds first`}
                    </button>
                    <p className="text-xs text-zinc-500 mt-1">Plays only that stretch in the player above, then stops. Nothing is saved. The real sample also fades in and out a little.</p>
                  </div>
                )}
                <button type="button" onClick={makeOne} disabled={starting || startBad} data-testid="admin-samples-go" className={`${btn} bg-cyan-400 text-black`}>
                  {starting ? "Starting…" : isMade ? `Cut this one again, ${seconds} seconds` : `Make this ${seconds}-second sample`}
                </button>
              </div>
            )}

            {missing.length > 1 && !picked && lengthSlider}
            {!picked && (missing.length > 1 || made.length > 0) && wordsBlock(false)}
            {missing.length > 1 && (
              <button type="button" data-testid="admin-samples-start" onClick={makeAll} disabled={starting} className={`${btn} border border-white/20 text-white hover:border-cyan-400/60 flex items-center justify-center gap-1.5`}>
                <Sparkles className="w-4 h-4" /> {starting ? "Starting…" : `Make all ${missing.length} at the best spot, ${seconds} seconds each`}
              </button>
            )}
            {made.length > 0 && (
              <div className="space-y-1" data-testid="admin-samples-updateall-box">
                <button type="button" data-testid="admin-samples-updateall" onClick={updateAll} disabled={starting} className={`${btn} border border-white/20 text-white hover:border-cyan-400/60 flex items-center justify-center gap-1.5`}>
                  <Sparkles className="w-4 h-4" /> {starting ? "Starting…" : wordsNow ? `Put this message on all ${made.length} sample${made.length === 1 ? "" : "s"}, same spots` : `Cut all ${made.length} sample${made.length === 1 ? "" : "s"} again with no words, same spots`}
                </button>
                <p className="text-xs text-zinc-500">Only the message changes. Each sample keeps its own start, length, color and place for the words. Visitors hear the old one until the new one is ready. Samples you put in by hand are not touched.</p>
              </div>
            )}
          </div>
        )}

        {info && !running && results.length > 0 && (
          <div data-testid="admin-samples-results">
            <div className="mb-1.5">
              {job.state === "error" ? "It stopped part way. " : ""}Last run{job.auto ? " (made automatically)" : ""}: {madeCount} of {results.length} sample{results.length === 1 ? "" : "s"} made.
            </div>
            <ul className="text-xs space-y-1 max-h-56 overflow-y-auto pr-1">
              {results.map((r) => (
                <li key={r.id} className={`flex items-start gap-1.5 ${r.ok ? "text-zinc-300" : "text-amber-300"}`}>
                  {r.ok ? <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-300" /> : <X className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                  <span className="min-w-0"><span className="font-semibold">{r.title}</span> — {r.ok ? r.note : `skipped, ${r.note}`}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
